import { useEffect } from 'react';

/**
 * Lightweight SEO hook for SPA pages.
 *
 * Sets document title + a small set of meta / Open Graph tags that social
 * platforms (iMessage / WhatsApp / Slack / LinkedIn) and browsers read for
 * link previews. Restores the previous values on unmount so navigating away
 * doesn't leak per-page data to other routes.
 *
 * Note: many social platforms scrape the static HTML. Dynamic SEO via JS
 * still helps for: browser tab, bookmarks, history, some modern crawlers, and
 * any share sheet that renders the page client-side (e.g. Safari tab groups).
 * Full preview-card fidelity for cold scrapers needs SSR / a prerender; that
 * is intentionally out of scope here.
 */
export function useSEO({ title, description, image, url } = {}) {
  useEffect(() => {
    const prev = {
      title: document.title,
      description: getMetaContent('name', 'description'),
      ogTitle: getMetaContent('property', 'og:title'),
      ogDescription: getMetaContent('property', 'og:description'),
      ogImage: getMetaContent('property', 'og:image'),
      ogUrl: getMetaContent('property', 'og:url'),
      ogType: getMetaContent('property', 'og:type'),
      twitterCard: getMetaContent('name', 'twitter:card'),
      twitterTitle: getMetaContent('name', 'twitter:title'),
      twitterDescription: getMetaContent('name', 'twitter:description'),
      twitterImage: getMetaContent('name', 'twitter:image'),
      canonical: getCanonical(),
    };

    if (title) document.title = title;
    if (description) setMeta('name', 'description', description);
    if (title) setMeta('property', 'og:title', title);
    if (description) setMeta('property', 'og:description', description);
    if (image) setMeta('property', 'og:image', image);
    if (image) setMeta('property', 'og:image:width', '1200');
    if (image) setMeta('property', 'og:image:height', '630');
    if (url) setMeta('property', 'og:url', url);
    if (url) setCanonical(url);
    setMeta('property', 'og:type', 'website');
    setMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    if (title) setMeta('name', 'twitter:title', title);
    if (description) setMeta('name', 'twitter:description', description);
    if (image) setMeta('name', 'twitter:image', image);

    return () => {
      document.title = prev.title;
      restoreMeta('name', 'description', prev.description);
      restoreMeta('property', 'og:title', prev.ogTitle);
      restoreMeta('property', 'og:description', prev.ogDescription);
      restoreMeta('property', 'og:image', prev.ogImage);
      restoreMeta('property', 'og:url', prev.ogUrl);
      restoreMeta('property', 'og:type', prev.ogType);
      restoreMeta('name', 'twitter:card', prev.twitterCard);
      restoreMeta('name', 'twitter:title', prev.twitterTitle);
      restoreMeta('name', 'twitter:description', prev.twitterDescription);
      restoreMeta('name', 'twitter:image', prev.twitterImage);
      if (prev.canonical) setCanonical(prev.canonical);
      else document.head.querySelector('link[rel="canonical"]')?.remove();
    };
  }, [title, description, image, url]);
}

/** Injects a JSON-LD <script> for the lifetime of the page. */
export function useJsonLd(data) {
  useEffect(() => {
    if (!data) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.dataset.seo = 'page';
    // textContent does not neutralise a literal `</script>`, so escape the
    // characters that could break out of the script element.
    el.textContent = JSON.stringify(data).replace(/</g, '\\u003c').replace(/&/g, '\\u0026');
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [data]);
}

function getCanonical() {
  const el = document.head.querySelector('link[rel="canonical"]');
  return el ? el.getAttribute('href') : null;
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function getMetaContent(attr, key) {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  return el ? el.getAttribute('content') : null;
}

function setMeta(attr, key, value) {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function restoreMeta(attr, key, value) {
  const el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) return;
  if (value === null || value === undefined) {
    el.remove();
  } else {
    el.setAttribute('content', value);
  }
}
